import type { TriStateCheck } from './spoolDetailFormUi';

/** Top-level choice: localized repair vs full pipe cut-out and re-weld. */
export type WeldingRepairWorkType = 'repair' | 'cutout';

export type WeldingRepairEntryForm = {
  workType: WeldingRepairWorkType;
  defectType: string;
  defectLocation: string;
  defectLengthMm: string;
  defectDepthMm: string;
  triggeringNdtReport: string;
  linkedNcr: string;
  repairProcedureRef: string;
  repairAttempt: string;
  excavationDepthMm: string;
  excavationNdtMethod: string;
  excavationNdtResult: string;
  excavationPreheatTempC: string;
  excavationQcVerified: TriStateCheck;
  repairLengthMm: string;
  repairedByWelderId: string;
  repairPreheatTempC: string;
  repairInterpassTempC: string;
  visualAfterRepair: 'pass' | 'fail' | 'pending';
  notes: string;
};

export const DEFAULT_WELDING_REPAIR_ENTRY: WeldingRepairEntryForm = {
  workType: 'repair',
  defectType: 'slag',
  defectLocation: '',
  defectLengthMm: '',
  defectDepthMm: '',
  triggeringNdtReport: '',
  linkedNcr: '',
  repairProcedureRef: '',
  repairAttempt: 'attempt1',
  excavationDepthMm: '',
  excavationNdtMethod: 'mpi',
  excavationNdtResult: 'pass',
  excavationPreheatTempC: '',
  excavationQcVerified: 'neutral',
  repairLengthMm: '',
  repairedByWelderId: '',
  repairPreheatTempC: '',
  repairInterpassTempC: '',
  visualAfterRepair: 'pending',
  notes: '',
};

function triFromApi(v: unknown): TriStateCheck {
  if (v === 'pass' || v === 'fail') return v;
  return 'neutral';
}

export function repairEntryFromPayload(raw: unknown): WeldingRepairEntryForm {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_WELDING_REPAIR_ENTRY };
  const o = raw as Record<string, unknown>;
  const workRaw = String(o.workType ?? o.work_type ?? 'repair').toLowerCase();
  const workType: WeldingRepairWorkType =
    workRaw === 'cutout' || workRaw === 'cut_out' ? 'cutout' : 'repair';
  return {
    workType,
    defectType: String(o.defectType ?? o.defect_type ?? 'slag'),
    defectLocation: String(o.defectLocation ?? o.defect_location ?? ''),
    defectLengthMm: String(o.defectLengthMm ?? o.defect_length_mm ?? ''),
    defectDepthMm: String(o.defectDepthMm ?? o.defect_depth_mm ?? ''),
    triggeringNdtReport: String(o.triggeringNdtReport ?? o.triggering_ndt_report ?? ''),
    linkedNcr: String(o.linkedNcr ?? o.linked_ncr ?? ''),
    repairProcedureRef: String(o.repairProcedureRef ?? o.repair_procedure_ref ?? ''),
    repairAttempt: String(o.repairAttempt ?? o.repair_attempt ?? 'attempt1'),
    excavationDepthMm: String(o.excavationDepthMm ?? o.excavation_depth_mm ?? ''),
    excavationNdtMethod: String(o.excavationNdtMethod ?? o.excavation_ndt_method ?? 'mpi'),
    excavationNdtResult: String(o.excavationNdtResult ?? o.excavation_ndt_result ?? 'pass'),
    excavationPreheatTempC: String(o.excavationPreheatTempC ?? o.excavation_preheat_temp_c ?? ''),
    excavationQcVerified: triFromApi(o.excavationQcVerified ?? o.excavation_qc_verified),
    repairLengthMm: String(o.repairLengthMm ?? o.repair_length_mm ?? ''),
    repairedByWelderId: String(o.repairedByWelderId ?? o.repaired_by_welder_id ?? ''),
    repairPreheatTempC: String(o.repairPreheatTempC ?? o.repair_preheat_temp_c ?? ''),
    repairInterpassTempC: String(o.repairInterpassTempC ?? o.repair_interpass_temp_c ?? ''),
    visualAfterRepair:
      o.visualAfterRepair === 'pass' || o.visualAfterRepair === 'fail'
        ? o.visualAfterRepair
        : o.visual_after_repair === 'pass' || o.visual_after_repair === 'fail'
          ? (o.visual_after_repair as 'pass' | 'fail')
          : 'pending',
    notes: String(o.notes ?? ''),
  };
}

export function repairEntryToPayload(form: WeldingRepairEntryForm): Record<string, unknown> {
  return {
    workType: form.workType,
    defectType: form.defectType,
    defectLocation: form.defectLocation,
    defectLengthMm: form.defectLengthMm,
    defectDepthMm: form.defectDepthMm,
    triggeringNdtReport: form.triggeringNdtReport,
    linkedNcr: form.linkedNcr,
    repairProcedureRef: form.repairProcedureRef,
    repairAttempt: form.repairAttempt,
    excavationDepthMm: form.excavationDepthMm,
    excavationNdtMethod: form.excavationNdtMethod,
    excavationNdtResult: form.excavationNdtResult,
    excavationPreheatTempC: form.excavationPreheatTempC,
    excavationQcVerified:
      form.excavationQcVerified === 'pass'
        ? 'pass'
        : form.excavationQcVerified === 'fail'
          ? 'fail'
          : 'pending',
    repairLengthMm: form.repairLengthMm,
    repairedByWelderId: form.repairedByWelderId,
    repairPreheatTempC: form.repairPreheatTempC,
    repairInterpassTempC: form.repairInterpassTempC,
    visualAfterRepair: form.visualAfterRepair,
    notes: form.notes,
  };
}

export function readRepairEntryFromWeldingPayload(pl: Record<string, unknown> | null): WeldingRepairEntryForm {
  if (!pl) return { ...DEFAULT_WELDING_REPAIR_ENTRY };
  const direct = pl.repairEntry ?? pl.repair_entry;
  if (direct) return repairEntryFromPayload(direct);
  return { ...DEFAULT_WELDING_REPAIR_ENTRY };
}
